import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface DashboardInvoice {
  invoice_id: string;
  filename: string;
  status: string;
  invoice_type?: string;
  reception_date?: string;
  due_date?: string;
  priority: string;
  priority_reason?: string;
}

interface TopCase {
  invoice_id: string;
  filename: string;
  status: string;
  due_date?: string;
  priority: string;
  priority_reason?: string;
}

interface DashboardProps {
  onInvoiceSelect: (filename: string) => void;
  onAddInvoice?: (file: File) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onInvoiceSelect, onAddInvoice }) => {
  const [invoices, setInvoices] = useState<DashboardInvoice[]>([]);
  const [topCases, setTopCases] = useState<TopCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load both invoices and top cases in parallel
      const [invoicesResponse, topCasesResponse] = await Promise.all([
        apiService.getDashboardInvoices(),
        apiService.getTopDashboardCases()
      ]);

      if (invoicesResponse.error) {
        setError(invoicesResponse.error);
      } else if (invoicesResponse.data) {
        setInvoices(invoicesResponse.data.invoices);
      }

      if (topCasesResponse.data) {
        setTopCases(topCasesResponse.data.top_cases);
      }
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'call for action':
        return 'bg-orange-100 text-orange-800';
      case 'workflow in progress':
        return 'bg-blue-100 text-blue-800';
      case 'complete':
        return 'bg-green-100 text-green-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'pending hitl':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
      case 'p1':
        return 'bg-red-100 text-red-800';
      case 'medium':
      case 'p2':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
      case 'p3':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityDisplayText = (priority: string) => {
    return priority === 'Medium' ? 'None' : priority;
  };

  const handleInvoiceClick = (filename: string) => {
    onInvoiceSelect(filename);
  };

  const getUniqueStatuses = () => {
    const statuses = [...new Set(invoices.map(invoice => invoice.status))];
    return statuses.sort();
  };

  const filteredInvoices = statusFilter === 'all'
    ? invoices
    : invoices.filter(invoice => invoice.status === statusFilter);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded-full animate-pulse"></div>
            <span className="text-gray-600">Loading dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Error Loading Dashboard</h3>
          <p className="text-red-600 mt-1">{error}</p>
          <button
            onClick={loadDashboardData}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-blue-50 transition-colors duration-200">
      <div className="flex-1 p-8">
        <header>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8 transition-colors duration-200">I2PA | Agentic AI</h1>
        </header>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-normal text-gray-900 dark:text-gray-100 transition-colors duration-200">Welcome !</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={loadDashboardData}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-purple-500/25 rounded-full"
            >
              Refresh
            </button>
            <label className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-full cursor-pointer hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-blue-500/25">
              Add Invoice
              <input
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={e => {
                  if (e.target.files && e.target.files[0] && onAddInvoice) {
                    onAddInvoice(e.target.files[0]);
                    e.target.value = ""; // Reset input so same file can be selected again if needed
                  }
                }}
              />
            </label>
          </div>
        </div>

        {/* Top 5 Cases Section */}
        <div className="mb-8">
          <h3 className="text-xl tracking-wider uppercase font-semibold text-gray-900 dark:text-gray-100 mb-4 transition-colors duration-200">Top 5 Cases</h3>
          {topCases.length === 0 ? (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 text-center transition-colors duration-200">
              <div className="text-gray-500 dark:text-gray-400 transition-colors duration-200">No priority cases found</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {topCases.map((case_item) => (
                <div
                  key={case_item.invoice_id}
                  onClick={() => handleInvoiceClick(case_item.filename)}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 cursor-pointer hover:shadow-lg transition-all duration-200 transform hover:scale-105 hover:shadow-purple-500/10"
                >
                  <div className="space-y-3">
                    {/* Invoice ID */}
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors duration-200">Invoice ID</div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate transition-colors duration-200" title={case_item.invoice_id}>
                        {case_item.invoice_id}
                      </div>
                    </div>

                    {/* Current Status */}
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors duration-200">Status</div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(case_item.status)}`}>
                        {case_item.status}
                      </span>
                    </div>

                    {/* Due Date */}
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors duration-200">Due Date</div>
                      <div className="text-sm text-gray-900 dark:text-gray-100 transition-colors duration-200">
                        {formatDate(case_item.due_date)}
                      </div>
                    </div>

                    {/* Priority */}
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(case_item.priority)}`}>
                        {getPriorityDisplayText(case_item.priority)}
                      </span>
                    </div>

                    {/* Priority Reason */}
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider transition-colors duration-200">Priority Reason</div>
                      <div className="text-xs text-gray-700 dark:text-gray-200 line-clamp-2 transition-colors duration-200" title={case_item.priority_reason || 'No reason provided'}>
                        {case_item.priority_reason || 'No reason provided'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* All Invoices Section */}
        <div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden min-h-screen transition-colors duration-200">
            <div className="flex items-center justify-between mb-4 mt-4 mx-4">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-200">All Invoices</h3>
              <div className="flex items-center gap-2">
                <label htmlFor="status-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors duration-200">
                  Filter by Status:
                </label>
                <select
                  id="status-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors duration-200"
                >
                  <option value="all">All Statuses</option>
                  {getUniqueStatuses().map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {invoices.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-500 dark:text-gray-400 text-lg mb-2 transition-colors duration-200">No invoices found</div>
                <div className="text-gray-400 dark:text-gray-500 transition-colors duration-200">Upload an invoice to get started</div>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-500 dark:text-gray-400 text-lg mb-2 transition-colors duration-200">No invoices found for selected status</div>
                <div className="text-gray-400 dark:text-gray-500 transition-colors duration-200">Try selecting a different status filter</div>
              </div>
            ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow transition-colors min-h-screen duration-200 p-8 border border-gray-200">
                    <div className="overflow-x-auto">
                      {/* <div className="border border-gray-200 rounded-md"> */}
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 transition-colors duration-200">
                          <thead className="bg-gray-50 dark:bg-gray-700 transition-colors duration-200">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors duration-200">
                                Invoice ID
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors duration-200">
                                File Name
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors duration-200">
                                Invoice Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors duration-200">
                                Reception Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors duration-200">
                                Due Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors duration-200">
                                Priority
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider transition-colors duration-200">
                                Current Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 transition-colors duration-200">
                            {filteredInvoices.map((invoice) => (
                              <tr
                                key={invoice.invoice_id}
                                onClick={() => handleInvoiceClick(invoice.filename)}
                                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200"
                              >
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 transition-colors duration-200">
                                  {invoice.invoice_id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 transition-colors duration-200">
                                  {invoice.filename}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 transition-colors duration-200">
                                  {invoice.invoice_type || 'Unknown'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 transition-colors duration-200">
                                  {formatDate(invoice.reception_date)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 transition-colors duration-200">
                                  {formatDate(invoice.due_date)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(invoice.priority)}`}>
                                    {getPriorityDisplayText(invoice.priority)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                                    {invoice.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      {/* </div> */}
                    </div>
                  </div>
            )}
          </div>
        </div>
    </div>
    </div>
  );
};